import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripeElementLocale } from "@stripe/stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicInvoiceDetails, useCreatePaymentIntent } from "../data/invoice";

// Map i18n language codes to Stripe locale codes
const getStripeLocale = (lang: string): StripeElementLocale => {
  const localeMap: Record<string, StripeElementLocale> = {
    en: "en",
    fr: "fr",
    es: "es",
    de: "de",
    pt: "pt",
    it: "it",
  };
  return localeMap[lang] || "auto";
};

// Distinguishes a fully-captured payment from a delayed/async one that
// Stripe reports as `processing` (the funds are not confirmed yet).
export type PaymentResultStatus = "succeeded" | "processing";

interface PaymentFormProps {
  invoiceData: PublicInvoiceDetails;
  stripeInstance: Stripe;
  onSuccess: (status: PaymentResultStatus) => void;
}

// Inner component that uses Stripe hooks (must be inside Elements provider)
function PaymentFormInner({
  invoiceData,
  email,
  onSuccess,
}: {
  invoiceData: PublicInvoiceDetails;
  email: string;
  onSuccess: (status: PaymentResultStatus) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const brandColor = invoiceData.business.brandColor || "#eb5e27";

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          receipt_email: email,
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(
          error.message || t("gymkeePay.paymentForm.paymentFailed"),
        );
      } else if (paymentIntent) {
        if (paymentIntent.status === "succeeded") {
          onSuccess("succeeded");
        } else if (paymentIntent.status === "processing") {
          onSuccess("processing");
        } else if (paymentIntent.status === "requires_action") {
          setErrorMessage(t("gymkeePay.paymentForm.authenticationRequired"));
        } else {
          setErrorMessage(
            t("gymkeePay.paymentForm.paymentStatus", {
              status: paymentIntent.status,
            }),
          );
        }
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      setErrorMessage(t("gymkeePay.paymentForm.unexpectedError"));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePaymentSubmit}>
      <div className="mb-4">
        <PaymentElement
          options={{
            layout: "tabs",
          }}
        />
      </div>

      {errorMessage && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full cursor-pointer rounded-lg px-4 py-3 font-medium text-white transition-all disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          backgroundColor: brandColor,
        }}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="h-5 w-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {t("gymkeePay.paymentForm.processing")}
          </span>
        ) : (
          t("gymkeePay.paymentForm.pay")
        )}
      </button>
    </form>
  );
}

function PaymentForm({
  invoiceData,
  stripeInstance,
  onSuccess,
}: PaymentFormProps) {
  const { t, i18n } = useTranslation();
  const [email] = useState(invoiceData.customer.email || "");

  const brandColor = invoiceData.business.brandColor || "#eb5e27";
  const stripeLocale = getStripeLocale(i18n.language);

  // PaymentIntent creation is a mutation (POST) so it fires exactly once,
  // never re-firing on window refocus / remount / reconnect (which would
  // create duplicate PaymentIntents).
  const createPaymentIntent = useCreatePaymentIntent();
  const {
    data: paymentIntentData,
    isError,
    error,
    mutate: createPaymentIntentMutate,
  } = createPaymentIntent;

  // Fire the PaymentIntent creation exactly once for this invoice/email.
  const hasRequestedRef = useRef(false);
  useEffect(() => {
    if (hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    createPaymentIntentMutate({ invoiceId: invoiceData.invoice.id, email });
  }, [createPaymentIntentMutate, invoiceData.invoice.id, email]);

  // Loading state — pending until the one-time mutation resolves.
  if (!paymentIntentData && !isError) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div
            className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-gray-200"
            style={{ borderTopColor: brandColor }}
          ></div>
          <p className="mt-3 text-sm text-gray-500">
            {t("gymkeePay.paymentForm.loadingPaymentForm")}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !paymentIntentData) {
    const errorMessage =
      (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message || t("gymkeePay.paymentForm.failedToInitialize");

    return (
      <div className="rounded-lg bg-red-50 p-4 text-center">
        <p className="text-sm text-red-600">{errorMessage}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 cursor-pointer text-sm font-medium hover:underline"
          style={{ color: brandColor }}
        >
          {t("gymkeePay.paymentForm.tryAgain")}
        </button>
      </div>
    );
  }

  // Payment form
  return (
    <Elements
      stripe={stripeInstance}
      options={{
        clientSecret: paymentIntentData.clientSecret,
        locale: stripeLocale,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: brandColor,
            borderRadius: "8px",
          },
        },
        loader: "auto",
      }}
    >
      <PaymentFormInner
        invoiceData={invoiceData}
        email={email}
        onSuccess={onSuccess}
      />
    </Elements>
  );
}

export default PaymentForm;
